<template>

  <div id="acuerdos-comerciales-en-curso">

    <!-- Loading copiado del app, para evitar condicion de carrera entre el redireccionamiento de la ruta y el montado de app -->
    <div v-if="loading_acuerdos_comerciales && mostrar_loading_principal" class="wrapper-loading">
      <v-progress-circular
        indeterminate
        size="100"
        width="10"
      >
      </v-progress-circular>
    </div>

    <ca-empty-state
      v-if="!acuerdos_comerciales_en_curso.length"
      icon="description"
      icon_outlined
      texto_principal="No hay acuerdos en curso"
      class="empty-state-custom"
    >
      <div slot="texto-secundario">
        <p class="texto-secundario">Aquí verás la lista de acuerdos comerciales <br> enviados que aún requieren tu atención</p>
      </div>
    </ca-empty-state>

    <div v-if="acuerdos_comerciales_en_curso.length">
      <tabla-acuerdos-comerciales
        ref="tabla_acuerdos_comerciales_en_curso"
        :loading="loading_acuerdos_comerciales"
        :acuerdos_comerciales="acuerdos_comerciales_en_curso"
      >
      </tabla-acuerdos-comerciales>
    </div>

  </div>

</template>

<script>
import { EventBus } from '@/main';
import TablaAcuerdosComerciales from './TablaAcuerdosComerciales.vue';
import { APIAcuerdoComercial } from '@/views/acuerdo-comercial/api/acuerdo-comercial.api';
import { ENDPOINT_GETTER } from '@/store/endpoints/endpoints.types';
import { AccionesSobreAcuerdosComerciales, TipoAcuerdo } from '../acuerdo-comercial.consts';

export default {
  name:'AcuerdosComercialesEnCurso',
  components:{
    TablaAcuerdosComerciales
  },
  data: () => ({
    loading_acuerdos_comerciales: false,

    acuerdos_comerciales_en_curso: [],
    mostrar_loading_principal: true, // Flag para indicar cuando mostrar el loading principal (se muestra una vez al montarse y luego se muestra el loading en la tabla cuando se hace el refresh del tab)
  }),
  created() {

    /**
     * Hook para reaccionar ante el evento de eliminar un acuerdo comercial
     * Eliminá el acuerdo comercial del arreglo de acuerdos comerciales en curso y de esta manera la tabla a través del watch detectará el cambio y se actualizará
     */
    EventBus.$on('acuerdo-comercial:eliminar', (acuerdo_comercial_eliminado) => {
      this.actualizarAcuerdoEliminado(acuerdo_comercial_eliminado);
    });

    /**
     * Hook para reaccionar ante el evento de extender el tiempo de trial de un acuerdo comercial
     * Actualiza el acuerdo comercial en el arreglo de acuerdos comerciales en curso y de esta manera la tabla a través del watch detectará el cambio y se actualizará
     */
    EventBus.$on('acuerdo-comercial:extender-trial', (acuerdo_comercial_extender_trial) => {
      this.actualizarAcuerdoTrialExtendido(acuerdo_comercial_extender_trial);
    });

    /**
     * Hook para reaccionar ante el evento de finalizar el periodo de aceptación de un acuerdo comercial
     */
    EventBus.$on('acuerdo-comercial:finalizar-trial', (acuerdo_comercial_finalizar_trial) => {
      this.actualizarAcuerdoFinalizarTrial(acuerdo_comercial_finalizar_trial);
    });

    /**
     * Hook para reaccionar ante el evento de editar un acuerdo comercial
     */
    EventBus.$on('acuerdo-comercial:editado', (acuerdo_comercial_editado) => {
      this.actualizarAcuerdoEditado(acuerdo_comercial_editado);
    });

    /**
     * Hook para reaccionar ante el evento de creación de acuerdos comerciales
     */
    EventBus.$on('acuerdo-comercial:crear', (acuerdos_comerciales_creados) => {
      this.actualizarAcuerdosCreados(acuerdos_comerciales_creados);
    });
  },
  /**
   * Elimino los listeners para evitar comportamientos inesperados
   */
  beforeDestroy() {
    EventBus.$off('acuerdo-comercial:eliminar');
    EventBus.$off('acuerdo-comercial:editado');
    EventBus.$off('acuerdo-comercial:crear');
    EventBus.$off('acuerdo-comercial:extender-trial');
    EventBus.$off('acuerdo-comercial:finalizar-trial');
  },
  async mounted(){
    await this.recuperarAcuerdosComercialesEnCurso();
    this.mostrar_loading_principal = false; // No mostramos más el loading principal una vez que se montó el componente (se mostrará el loading en la tabla)
  },
  methods: {

    /**
     * Método para recuperar todos los acuerdos comerciales en curso
     */
    async recuperarAcuerdosComercialesEnCurso() {

      const url = this.$store.getters[ENDPOINT_GETTER]('propuestas_pendientes');
      if(!url) {
        return;
      }

      this.loading_acuerdos_comerciales = true;
      await APIAcuerdoComercial.getAcuerdosComerciales(url)
        .then((resp) => {
          this.acuerdos_comerciales_en_curso = resp;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading_acuerdos_comerciales = false;
        });
    },

    /**
     * Metodo para eliminar un acuerdo de la lista, para evitar hacer un nuevo GET de la lista de este componente
     */
    actualizarAcuerdoEliminado(acuerdo_eliminado) {
      const indice = this.acuerdos_comerciales_en_curso.findIndex(acuerdo_comercial => acuerdo_comercial.id === acuerdo_eliminado.id);
      if (indice !== -1){
        this.acuerdos_comerciales_en_curso.splice(indice, 1); // Eliminación del acuerdo comercial del arreglo de acuerdos comerciales

        // Calculamos headers nuevamente porque puede que el AC eliminado sea el que agregó las columnas opcionales
        this.actualizarHeadersTabla();
      }
    },

    /**
     * Metodo para actualizar todos los atributos referidos al estado del trial
     * Con esto evitamos hacer un nuevo GET de la lista de este componente
     */
    actualizarAcuerdoTrialExtendido(acuerdo_trial_extendido) {
      const indice = this.acuerdos_comerciales_en_curso.findIndex(acuerdo_comercial => acuerdo_comercial.id === acuerdo_trial_extendido.id);
      if (indice !== -1){
        this.acuerdos_comerciales_en_curso[indice].propuesta_consorcio.dias_restantes = acuerdo_trial_extendido.propuesta_consorcio.dias_restantes;
        this.acuerdos_comerciales_en_curso[indice].propuesta_consorcio.fecha_fin_trial = acuerdo_trial_extendido.propuesta_consorcio.fecha_fin_trial;
        this.acuerdos_comerciales_en_curso[indice].fecha_cambio_estado_actual = acuerdo_trial_extendido.fecha_cambio_estado_actual;

        if(this.acuerdos_comerciales_en_curso[indice].expirada) {
          this.acuerdos_comerciales_en_curso[indice].expirada = false;
        }

        // Si el acuerdo comercial no tiene la acción de finalizar período de aceptación, se la agregamos
        if (!this.acuerdos_comerciales_en_curso[indice].acciones_posibles.includes(AccionesSobreAcuerdosComerciales.FINALIZAR_PERIODO_ACEPTACION)) {
          this.acuerdos_comerciales_en_curso[indice].acciones_posibles.push(AccionesSobreAcuerdosComerciales.FINALIZAR_PERIODO_ACEPTACION);
        }

        this.actualizarHeadersTabla();
      }
    },

    /**
     * Metodo que actualiza todos los atributos referidos al estado del trial, ahora el estado del acuerdo es expirado
     * Con esto evitamos hacer un nuevo GET de la lista de este componente
     */
    actualizarAcuerdoFinalizarTrial(acuerdo_trial_finalizado) {
      const indice = this.acuerdos_comerciales_en_curso.findIndex(acuerdo_comercial => acuerdo_comercial.id === acuerdo_trial_finalizado.id);

      if(indice !== -1) {
        this.acuerdos_comerciales_en_curso[indice].propuesta_consorcio.dias_restantes = null;
        this.acuerdos_comerciales_en_curso[indice].propuesta_consorcio.fecha_fin_trial = acuerdo_trial_finalizado.propuesta_consorcio.fecha_fin_trial;
        this.acuerdos_comerciales_en_curso[indice].fecha_cambio_estado_actual = acuerdo_trial_finalizado.fecha_cambio_estado_actual;
        this.acuerdos_comerciales_en_curso[indice].expirada = true;
        this.acuerdos_comerciales_en_curso[indice].acciones_posibles = this.acuerdos_comerciales_en_curso[indice].acciones_posibles.filter(accion => accion !== AccionesSobreAcuerdosComerciales.FINALIZAR_PERIODO_ACEPTACION); // Una vez finalizado el período de aceptación, ya no se puede volver a finalizarlo
      }

      this.actualizarHeadersTabla();
    },

    /**
     * Metodo para actualizar los acuerdos filtradas con el acuerdo comercial editado
     */
    actualizarAcuerdoEditado(acuerdo_editado){
      let index = this.acuerdos_comerciales_en_curso.findIndex((acuerdo_comercial) => acuerdo_comercial.id === acuerdo_editado.id);
      index !== -1 ? this.acuerdos_comerciales_en_curso.splice(index, 1, acuerdo_editado) : null;
    },

    /**
     * Metodo para actualizar la lista de acuerdos con los nuevos acuerdos.
     * Se agregan al final de la lista de acuerdos porque al ser acuerdos nuevos son los que tienen el estado con menos prioridad.
     * 
     * Aclaración: como el tab de acuerdos en curso solo muestra pendientes, los acuerdos comerciales por defecto serán filtrados y exlcuidos al momento de agregarlos a la lista de acuerdos comerciales en curso.
     */
    actualizarAcuerdosCreados(acuerdos_comerciales_creados) {
      const acuerdos_comerciales_creados_para_consorcios = acuerdos_comerciales_creados.filter(acuerdo => acuerdo.tipo_propuesta !== TipoAcuerdo.ADMINISTRACION);

      // Agrego los nuevos acuerdos al final de la lista (puede ser uno o varios)
      this.acuerdos_comerciales_en_curso.push(...acuerdos_comerciales_creados_para_consorcios);

      // Calculamos headers nuevamente porque puede que el AC agregado deba mostrar las columnas opcionales
      this.actualizarHeadersTabla();
    },

    /**
     * Metodo para actualizar los headers de la tabla
     * Debido a que, con los cambios nuevos, se puede habilitar columnas opcionales que antes no se estaban mostrando o viceversa
     */
    actualizarHeadersTabla() {
      if(this.$refs.tabla_acuerdos_comerciales_en_curso) {
        this.$refs.tabla_acuerdos_comerciales_en_curso.actualizarMostrarColumnasOpcionales();
      }
    }
  },
};
</script>

<style lang="scss">
#acuerdos-comerciales-en-curso {

  .empty-state-custom{
    margin-top: 109px;
  }

  // Estilos copiados de app, para considerar mismo estilo
  .wrapper-loading {
    position: fixed;
    left: 0;
    top: 124px;
    background-color: color(blanco);
    opacity: 0.8;
    z-index: 500;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

}
</style>